import React, { useState, useContext, useEffect, useReducer } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import settingsReducer from "./settingsReducer";
import * as api from "../services";
import { SettingsProvider } from "./settingsContext";
import MutationMessage from "../Components/MutationMessage";
import CenterTabs from "./CenterTabs";
import useAccountSettings, { initUserSettings } from "./useAccountSettings";
import LogoutButton from "./LogoutButton";
import AppContext from "../appContext";
import { getValidationMessages, validationRules } from "./validationRules";
import LogoutConfirmationDialog from "./LogoutConfirmationDialog";

export default function ManageAccount(props) {
  const {
    app: { sessionId },
  } = useContext(AppContext);
  const projectId = props?.location?.state?.projectId ?? 0;
  const { t } = useTranslation();
  const [settings, settingsDispatch] = useReducer(
    settingsReducer,
    initUserSettings.settings
  );
  const [openLogoutConfirmationDialog, setOpenLogoutConfirmationDialog] =
    useState(false);

  const disableSubmit = () => {
    // form should not contain errors. Map all setting keys through the validation
    const hasErrors =
      Object.keys(settings)
        .map((key) => validateInput(key))
        .filter((validationObj) => validationObj.isValid === false).length > 0;
    // at least one value should be changed OR correspondence email needs to be verified
    const isChanged =
      JSON.stringify(settings) === JSON.stringify(accountSettings);
    return hasErrors || isChanged;
  };

  // server state
  const { accountSettings, refetchSettings } = useAccountSettings({
    projectId,
  });
  const mutation = useMutation(
    () => api.postUserSettings({ ...settings, sessionId, projectId }),
    {
      onSuccess: (data) => {
        if (data.data?.status === 1) {
          if (settings.dashboard !== accountSettings.dashboard) {
            setOpenLogoutConfirmationDialog(true);
          }
          refetchSettings();
        }
      },
    }
  );

  useEffect(() => {
    settingsDispatch({ type: "SET_SETTINGS", payload: accountSettings });
  }, [accountSettings]);

  function validateInput(fieldId) {
    // Default error messages based on field ID
    const value = settings[fieldId];
    const rules = validationRules[fieldId] || {};
    const errorMessages = getValidationMessages(t);
    const messages = errorMessages[fieldId] || {};

    if (rules.empty && value.length === 0) {
      return {
        isValid: false,
        message: messages.empty || "This field cannot be empty.",
        tab: messages.tab,
      };
    }

    if (rules.minLength && value.length < rules.minLength) {
      return {
        isValid: false,
        message:
          messages.minLength ||
          `Minimum length is ${rules.minLength} characters.`,
        tab: messages.tab,
      };
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      return {
        isValid: false,
        message:
          messages.maxLength ||
          `Maximum length is ${rules.maxLength} characters.`,
        tab: messages.tab,
      };
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      return {
        isValid: false,
        message: messages.pattern || "Invalid format.",
        tab: messages.tab,
      };
    }

    // custom check for email verification
    if (accountSettings.email2?.length > 0 && !accountSettings.email2Verified)
      return {
        isValid: true,
        message: t("account.errors.email2Verified"),
        tab: messages.tab,
      };

    // If all checks pass
    return {
      isValid: true,
      message: "",
      tab: "null",
    };
  }

  const handleChange = (event) => {
    if (
      [
        "makePublic",
        "findOnName",
        "mailOnAdded",
        "mailOnNews",
        "mailOnResearch",
      ].includes(event.target.name)
    ) {
      // update checkbox
      settingsDispatch({
        type: "UPDATE_SETTINGS",
        payload: { [event.target.name]: +event.target.checked },
      });
    } else {
      settingsDispatch({
        type: "UPDATE_SETTINGS",
        payload: { [event.target.name]: event.target.value },
      });
    }
  };

  const handleSubmit = () =>
    !disableSubmit() ? mutation.mutate() : alert(t("account.errors.save"));

  return (
    <SettingsProvider value={{ settings, handleChange, validateInput }}>
      <MutationMessage
        mutation={mutation}
        text={{
          loading: t("common.notifications.updateLoading"),
          success: t("common.notifications.updateSuccess"),
        }}
      />
      <Box
        sx={{ backgroundColor: "background.paper" }}
        display="flex"
        justifyContent={"center"}
        padding={3}
        gap={2}
      >
        <ListItem
          sx={{
            width: "fit-content",
          }}
        >
          <ListItemIcon>
            <Avatar
              sx={{
                color: "common.white",
                borderRadius: 1,
                width: "48px",
                height: "48px",
                marginRight: 2,
              }}
            >
              <Typography>
                {`${accountSettings.firstName.charAt(
                  0
                )}${accountSettings.lastName.charAt(0)}`}
              </Typography>
            </Avatar>
          </ListItemIcon>
          <ListItemText>
            <Typography variant="h6">{`${accountSettings.firstName} ${accountSettings.lastName}`}</Typography>
            <Tooltip title={t("account.code")}>
              <Typography
                variant="body1"
                color="textSecondary"
              >{`@${accountSettings.code}`}</Typography>
            </Tooltip>
          </ListItemText>
        </ListItem>
        {projectId === 0 ? <LogoutButton /> : null}
      </Box>
      <CenterTabs />
      <Box width={"fit-content"} margin={"auto"}>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={disableSubmit()}
        >
          {t("common.actions.update")}
        </Button>
      </Box>
      <LogoutConfirmationDialog
        open={openLogoutConfirmationDialog}
        setOpen={setOpenLogoutConfirmationDialog}
      />
    </SettingsProvider>
  );
}
